// EmployeeAppointments.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Tooltip,
  Chip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ApiServices from "../../Services/ApiServices/ApiServices";

const BASE_URL = "http://127.0.0.1:8000";

const EmployeeAppointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);

  // upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadAppointment, setUploadAppointment] = useState(null);
  const [uploadServiceId, setUploadServiceId] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadIsBefore, setUploadIsBefore] = useState("true");
  const [uploadLoading, setUploadLoading] = useState(false);

  // snack
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  // get logged-in user (employee) from sessionStorage
  const getSessionUser = () => {
    try {
      const raw = sessionStorage.getItem("user");
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };
  const user = getSessionUser();
  const employeeId = user?.id ?? null;

  // fetch and filter appointments assigned to this employee
  const fetchAppointments = async () => {
    if (!employeeId) {
      setSnack({ open: true, message: "No logged-in user found in sessionStorage ('user')", severity: "error" });
      return;
    }
    setLoading(true);
    try {
      const res = await ApiServices.get("service-appointments/");
      const data = res?.data ?? res;
      if (!Array.isArray(data)) {
        setAppointments([]);
        setSnack({ open: true, message: "Unexpected data from server", severity: "error" });
      } else {
        const filtered = data
          .filter((a) => {
            if (!a.employee) return false;
            if (typeof a.employee === "object") return Number(a.employee.id) === Number(employeeId);
            return Number(a.employee) === Number(employeeId);
          })
          .map((a) => ({ ...a, _scheduled_at_date: a.scheduled_at ? new Date(a.scheduled_at) : null }));
        filtered.sort((x, y) => {
          const xd = x._scheduled_at_date ? x._scheduled_at_date.getTime() : 0;
          const yd = y._scheduled_at_date ? y._scheduled_at_date.getTime() : 0;
          return yd - xd;
        });
        setAppointments(filtered);
      }
    } catch (err) {
      console.error(err);
      let msg = "Failed to fetch appointments.";
      if (err?.response?.data?.detail) msg = err.response.data.detail;
      else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // date formatting helper
  const formatDateTime = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${y}-${m}-${day} ${hh}:${mm}`;
    } catch {
      return iso;
    }
  };

  const openImageInNewTab = (path) => {
    const url = path?.startsWith("http") ? path : `${BASE_URL}${path}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // upload dialog handlers
  const openUploadDialog = (appointment, service = null) => {
    setUploadAppointment(appointment);
    const firstService = (service || (appointment.appointment_services && appointment.appointment_services[0])) || null;
    setUploadServiceId(firstService ? firstService.id : "");
    setUploadIsBefore("true");
    setUploadFile(null);
    setUploadOpen(true);
  };

  const closeUploadDialog = () => {
    setUploadOpen(false);
    setUploadAppointment(null);
    setUploadServiceId("");
    setUploadFile(null);
    setUploadIsBefore("true");
    setUploadLoading(false);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    setUploadFile(f);
  };

  const submitUpload = async () => {
    if (!uploadAppointment || !uploadServiceId) {
      setSnack({ open: true, message: "Please select an appointment service to upload for", severity: "error" });
      return;
    }
    if (!uploadFile) {
      setSnack({ open: true, message: "Please select an image file to upload", severity: "error" });
      return;
    }

    setUploadLoading(true);
    try {
      const form = new FormData();
      form.append("image", uploadFile);
      form.append("is_before", uploadIsBefore === "true" ? "true" : "false");
      form.append("uploaded_by", "employee");

      await ApiServices.post(`service-appointment-items/${uploadServiceId}/upload-image/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSnack({ open: true, message: "Image uploaded successfully", severity: "success" });
      closeUploadDialog();
      await fetchAppointments();
    } catch (err) {
      console.error(err);
      let msg = "Failed to upload image.";
      if (err?.response?.data) {
        const d = err.response.data;
        if (typeof d === "string") msg = d;
        else if (d?.detail) msg = d.detail;
        else msg = JSON.stringify(d);
      } else if (err?.message) msg = err.message;
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setUploadLoading(false);
    }
  };

  // chips mapping (status & payment)
  const statusToChipProps = (status) => {
    const s = (status ?? "").toString().toUpperCase();
    switch (s) {
      case "COMPLETED":
        return { label: "Completed", color: "success" };
      case "NEW":
        return { label: "New", color: "warning" };
      case "PENDING":
        return { label: "Pending", color: "warning" };
      case "ASSIGNED_EMPLOYEE":
      case "ASSIGNED":
        return { label: "Assigned", color: "info" };
      case "CANCELLED":
      case "CANCELED":
        return { label: "Cancelled", color: "error" };
      default:
        return { label: status ?? "Unknown", color: "default" };
    }
  };

  const paymentStatusToChipProps = (pStatus) => {
    const s = (pStatus ?? "").toString().toUpperCase();
    switch (s) {
      case "PAID":
        return { label: "Paid", color: "success" };
      case "PENDING":
        return { label: "Pending", color: "warning" };
      case "FAILED":
        return { label: "Failed", color: "error" };
      default:
        return { label: pStatus ?? "Unknown", color: "default" };
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%", boxSizing: "border-box", minHeight: "70vh", background: "#fff" }}>
      <Typography variant="h5" sx={{ color: "#094c9f", fontWeight: 700, mb: 2 }}>
        My Assigned Appointments
      </Typography>

      <Paper sx={{ width: "100%", overflow: "hidden" }} elevation={2}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: "70vh", overflowX: "auto" }}>
            <Table stickyHeader sx={{ minWidth: 1000, tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 60 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 160 }}>Scheduled</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 220 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Business</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 200 }}>Services</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 110 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 120 }}>Payment</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 200 }}>Images</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 200 }}>Action</TableCell>
               
                </TableRow>
              </TableHead>

              <TableBody>
                {appointments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No appointments assigned to you.
                    </TableCell>
                  </TableRow>
                ) : (
                  appointments.map((a, idx) => {
                    const services = Array.isArray(a.appointment_services) ? a.appointment_services : [];
                    const images = [];
                    services.forEach((s) => {
                      if (Array.isArray(s.images) && s.images.length) {
                        s.images.forEach((im) => images.push({ ...im, appointment_service_id: s.id }));
                      }
                    });

                    const statusChip = statusToChipProps(a.status);
                    const paymentChip = paymentStatusToChipProps(a.payment_status);

                    return (
                      <TableRow key={a.id} hover>
                        <TableCell>{idx + 1}</TableCell>

                        <TableCell>
                          <Typography variant="body2">{formatDateTime(a.scheduled_at)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.address ?? ""}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip label={statusChip.label} color={statusChip.color} size="small" />
                        </TableCell>

                        <TableCell>
                          {a.customer_details ? (
                            <>
                              <Typography sx={{ fontWeight: 700 }}>{`${a.customer_details.first_name ?? ""} ${a.customer_details.last_name ?? ""}`}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Email: {a.customer_details.email ?? "-"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Phone: {a.customer_details.phone ?? "-"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Address: {a.customer_details.address ?? "-"} {a.customer_details.zip_code ? `• ${a.customer_details.zip_code}` : ""}
                              </Typography>
                            </>
                          ) : (
                            <Typography color="text.secondary">Customer not available</Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          {a.business ? (
                            <>
                              <Typography sx={{ fontWeight: 700 }}>{a.business.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {a.business.email} • {a.business.phone}
                              </Typography>
                            </>
                          ) : (
                            <Typography color="text.secondary">Not assigned</Typography>
                          )}
                        </TableCell>

                        <TableCell>
                          {/* {services.map((s) => (
                            <Box key={s.id} sx={{ mb: 0.8 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                                Service #{s.service} — ₹{parseFloat(s.price_snapshot ?? 0).toFixed(2)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Status: {s.status}
                              </Typography>
                              <Box sx={{ mt: 0.5 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<UploadFileIcon />}
                                  onClick={() => openUploadDialog(a, s)}
                                  sx={{ textTransform: "none" }}
                                >
                                  Upload image
                                </Button>
                              </Box>
                            </Box>
                          ))} */}
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>{parseFloat(a.total_price ?? 0).toFixed(2)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Admin cost: {a.admin_cost_to_business ?? "-"}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 700 }}>₹{parseFloat(a.total_price ?? 0).toFixed(2)}</Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip label={paymentChip.label} color={paymentChip.color} size="small" />
                          </Box>
                          {a.notes ? <Typography variant="caption" color="text.secondary">{a.notes.slice(0, 80)}</Typography> : null}
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                            {images.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">
                                No images
                              </Typography>
                            ) : (
                              images.map((im) => {
                                const url = im.image?.startsWith("http") ? im.image : `${BASE_URL}${im.image}`;
                                const label = im.is_before ? "Before" : "After";
                                return (
                                  <Tooltip key={im.id} title={`${label} • ${im.uploaded_by}`}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <img
                                        src={url}
                                        alt={label}
                                        style={{ width: 80, height: 60, objectFit: "cover", borderRadius: 6, cursor: "pointer", border: "1px solid rgba(0,0,0,0.08)" }}
                                        onClick={() => openImageInNewTab(im.image)}
                                      />
                                      <IconButton size="small" onClick={() => openImageInNewTab(im.image)} aria-label="open">
                                        <OpenInNewIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Tooltip>
                                );
                              })
                            )}
                          </Box>
                        </TableCell>

                        <TableCell align="right">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => openUploadDialog(a)}
                              startIcon={<UploadFileIcon />}
                              sx={{ textTransform: "none" }}
                            >
                              Upload
                            </Button>

                            {/* Employees cannot mark appointment as complete from UI (per requirement) */}
                            
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={closeUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Upload image for appointment #{uploadAppointment?.id ?? ""}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="service-select-label">Appointment service</InputLabel>
              <Select
                labelId="service-select-label"
                value={uploadServiceId}
                label="Appointment service"
                onChange={(e) => setUploadServiceId(e.target.value)}
              >
                {(uploadAppointment?.appointment_services ?? []).map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {`Service #${s.service} — ₹${parseFloat(s.price_snapshot ?? 0).toFixed(2)}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Image file
              </Typography>
              <input type="file" accept="image/*" onChange={handleFileChange} />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Type
              </Typography>
              <Box>
                <label style={{ marginRight: 12 }}>
                  <input type="radio" name="is_before" checked={uploadIsBefore === "true"} onChange={() => setUploadIsBefore("true")} /> Before
                </label>
                <label>
                  <input type="radio" name="is_before" checked={uploadIsBefore === "false"} onChange={() => setUploadIsBefore("false")} /> After
                </label>
              </Box>
            </Box>

            <Typography variant="caption" color="text.secondary">
              Uploaded images will be visible to business and customers. File size should be reasonable.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeUploadDialog} disabled={uploadLoading}>
            Cancel
          </Button>
          <Button onClick={submitUpload} variant="contained" disabled={uploadLoading} sx={{ backgroundColor: "#094c9f" }}>
            {uploadLoading ? <CircularProgress size={18} /> : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4500} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeeAppointments;
